import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { CanvaImportStatusDto } from "shared";
import { useCompany } from "../company/useCompany";
import { useMediaActions } from "../content/useMediaActions";
import {
	broadcastCanvaImport,
	CANVA_EDITOR_OPENED_EVENT,
	CANVA_SYNC_CHANNEL,
	type CanvaSyncMessage,
	clearCanvaEditorOpened,
	parseCanvaImportId,
	wasCanvaEditorOpened,
} from "./canvaEditorSession";
import { useCanvaImportStream } from "./useCanvaImportStream";

const MEDIA_POLL_MS = 5_000;
const MEDIA_POLL_FOR_MS = 5 * 60 * 1_000;

export function useCanvaMediaSync(): void {
	const [searchParams, setSearchParams] = useSearchParams();
	const { id: companyId } = useCompany();
	const { invalidateMedia } = useMediaActions();
	const importIdFromUrl = parseCanvaImportId(searchParams.get("canvaImport"));
	const [importId, setImportId] = useState<number | null>(importIdFromUrl);
	const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
	const pollDeadline = useRef<ReturnType<typeof setTimeout> | null>(null);

	const stopMediaPolling = useCallback(() => {
		if (pollTimer.current) {
			clearInterval(pollTimer.current);
			pollTimer.current = null;
		}
		if (pollDeadline.current) {
			clearTimeout(pollDeadline.current);
			pollDeadline.current = null;
		}
	}, []);

	const startMediaPolling = useCallback(() => {
		void invalidateMedia();
		if (pollTimer.current) {
			return;
		}
		pollTimer.current = setInterval(() => {
			void invalidateMedia();
		}, MEDIA_POLL_MS);
		pollDeadline.current = setTimeout(() => {
			stopMediaPolling();
		}, MEDIA_POLL_FOR_MS);
	}, [invalidateMedia, stopMediaPolling]);

	useEffect(() => {
		if (importIdFromUrl) {
			setImportId(importIdFromUrl);
			broadcastCanvaImport(importIdFromUrl);
			startMediaPolling();
		}
	}, [importIdFromUrl, startMediaPolling]);

	useEffect(() => {
		let channel: BroadcastChannel | null = null;
		try {
			channel = new BroadcastChannel(CANVA_SYNC_CHANNEL);
			channel.onmessage = (event: MessageEvent<CanvaSyncMessage>) => {
				if (event.data?.type === "canva-import" && event.data.importId > 0) {
					setImportId(event.data.importId);
					startMediaPolling();
					return;
				}
				if (event.data?.type === "canva-editor-opened") {
					startMediaPolling();
				}
			};
		} catch {
			channel = null;
		}

		return () => {
			channel?.close();
		};
	}, [startMediaPolling]);

	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState !== "visible") {
				return;
			}
			if (wasCanvaEditorOpened() || importId !== null) {
				startMediaPolling();
			}
		};

		const onEditorOpened = () => {
			startMediaPolling();
		};

		window.addEventListener("focus", onVisible);
		document.addEventListener("visibilitychange", onVisible);
		window.addEventListener(CANVA_EDITOR_OPENED_EVENT, onEditorOpened);

		if (wasCanvaEditorOpened() || importId !== null) {
			startMediaPolling();
		}

		return () => {
			window.removeEventListener("focus", onVisible);
			document.removeEventListener("visibilitychange", onVisible);
			window.removeEventListener(CANVA_EDITOR_OPENED_EVENT, onEditorOpened);
		};
	}, [importId, startMediaPolling]);

	const handleImportStatus = useCallback(
		(status: CanvaImportStatusDto) => {
			const importedCount = status.items.filter(
				(item) => item.contentFileId != null,
			).length;
			if (importedCount > 0) {
				void invalidateMedia();
			}
			if (status.status !== "completed" && status.status !== "failed") {
				return;
			}
			void invalidateMedia();
			clearCanvaEditorOpened();
			stopMediaPolling();
			setImportId(null);
			if (searchParams.has("canvaImport")) {
				const next = new URLSearchParams(searchParams);
				next.delete("canvaImport");
				setSearchParams(next, { replace: true });
			}
		},
		[invalidateMedia, searchParams, setSearchParams, stopMediaPolling],
	);

	useCanvaImportStream(companyId, importId, handleImportStatus);

	useEffect(() => {
		return () => {
			stopMediaPolling();
		};
	}, [stopMediaPolling]);
}
